import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import * as moment from 'moment';
import { noop } from 'rxjs';
import { LanguageService } from '../../../core/service/language.service';
import { LsnErrorStateMatcher } from '../error-state-matcher';

export const CUSTOM_DATEPICKER_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DatepickerComponent),
    multi: true
};

@Component({
    selector: 'app-datepicker',
    templateUrl: './datepicker.component.html',
    providers: [CUSTOM_DATEPICKER_CONTROL_VALUE_ACCESSOR]
})
export class DatepickerComponent implements ControlValueAccessor {

    @Input() disabled: boolean;
    @Input() required = false;

    @Input() errorStateMatcher: LsnErrorStateMatcher;
    @Input() placeholder: string;
    @Input() label: string;

    @Input() minToday = false;
    @Input() minTomorrow = false;
    @Input() min: Date = null;
    @Input() maxToday = false;
    @Input() max: Date = null;
    @Input() withTime = false;
    @Input() slim = false;
    @Input() startingDate = null;
    @Input() startingView = 'month';

    today = moment().startOf('day').toDate();
    tomorrow = moment().add(1, 'day').startOf('day').toDate();

    calendarValue: Date;
    value: string = null;

    get viewDateFormat() {
        return this.withTime
            ? 'DD-MM-YYYY HH:mm'
            : 'DD-MM-YYYY';
    }

    get mask() {
        return {
            mask: this.withTime
                ? '00-00-0000 00:00'
                : '00-00-0000',
            lazy: false
        }
    }

    private onChangeCallback: (_: any) => void = noop;
    private onTouchedCallback: (_: any) => void = noop;

    get startAt() {
        if (this.calendarValue !== null) {
            return this.calendarValue;
        } else if (this.startingDate) {
            return this.startingDate;
        } else {
            return null;
        }
    };

    get startView() {
        if (this.calendarValue !== null) {
            return 'month';
        } else {
            return this.startingView;
        }
    }


    constructor(private dateAdapter: DateAdapter<Date>,
                private languageService: LanguageService,
    ) {
        this.dateAdapter.setLocale(languageService.getMaterialLanguageCode());
    }

    get modelDateFormat() {
        if (this.withTime) {
            return 'YYYY-MM-DDTHH:mm:00';
        } else {
            return 'YYYY-MM-DD';
        }
    }

    get minDate(): Date {
        if (this.minToday) {
            return this.today;
        } else if (this.minTomorrow) {
            return this.tomorrow;
        }
        return this.min;
    }

    get maxDate(): Date {
        if (this.maxToday) {
            return this.today;
        }
        return this.max;
    }

    writeValue(value: any): void {
        if (value) {
            this.value = moment(value).format(this.viewDateFormat);
            this.calendarValue = moment(value).toDate();
        } else {
            this.value = null;
            this.calendarValue = null;
        }
    }

    registerOnChange(fn: any): void {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouchedCallback = fn;
    }

    fromInput($event) {
        if ($event.target.value.indexOf('_') !== -1) {
            this.update(null);
        } else if (this.withTime) {
            this.update(moment($event.target.value, this.viewDateFormat));
        } else {
            this.update(moment($event.target.value, this.viewDateFormat).startOf('day'));
        }
    }

    fromCalendar($event) {
        this.update(moment($event.target.value).startOf('day'));
    }

    update(value) {
        if (value === null) {
            this.onChangeCallback(null);
            this.writeValue(null);
            return;
        }

        const date = this.withTime
            ? moment(value, this.viewDateFormat)
            : moment(value, this.viewDateFormat).startOf('day');
        if ((this.maxToday && date.diff(moment()) > 0) ||
            (this.max && date.diff(moment(this.max).startOf('day')) > 0) ||
            (this.min && date.diff(moment(this.min).startOf('day')) < 0)) {
            this.onChangeCallback(null);
            this.writeValue(null);
        } else {
            this.writeValue(date);
            this.onChangeCallback(date.format(this.modelDateFormat));
        }
    }
}
